interface OrderChangeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderInfo: {
    name?: string;
    email?: string;
    garment_name?: string;
    color_name?: string;
    color_hex_code?: string;
    size_name?: string;
    previous_order_price?: number;
    final_price?: number;
    price_difference?: number;
    total_discount?: number;
  };
}

export default function OrderChangeConfirmationModal({ isOpen, onClose, orderInfo }: OrderChangeConfirmationModalProps) {
  if (!isOpen) return null;

  const formatPrice = (value: number): string => {
    const formattedValue = parseFloat(value.toFixed(2));
    return `${formattedValue.toFixed(2)}€`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[99999]">
      <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in">
        {/* Icono de éxito */}
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Título */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">¡Cambios Guardados!</h2>
        <p className="text-center text-gray-600 mb-6 text-sm">
          Los cambios en tu pedido se han guardado correctamente.
        </p>

        {/* Información del pedido */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <h3 className="font-semibold text-sm text-gray-700 mb-3">Resumen del cambio</h3>
          
          <div className="space-y-2 text-sm">
            {orderInfo.garment_name && (
              <div className="flex justify-between">
                <span className="text-gray-600">Prenda:</span>
                <span className="font-medium text-gray-800">{orderInfo.garment_name}</span>
              </div>
            )}
            
            {orderInfo.color_name && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Color:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">{orderInfo.color_name}</span>
                  {orderInfo.color_hex_code && (
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-300" 
                      style={{ backgroundColor: orderInfo.color_hex_code }}
                    ></div>
                  )}
                </div>
              </div>
            )}
            
            {orderInfo.size_name && (
              <div className="flex justify-between">
                <span className="text-gray-600">Talla:</span>
                <span className="font-medium text-gray-800">{orderInfo.size_name}</span>
              </div>
            )}
          </div>

          {/* Información de precios */}
          {orderInfo.previous_order_price !== undefined && orderInfo.final_price !== undefined && (
            <div className="mt-4 pt-3 border-t border-gray-200 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Pedido anterior:</span>
                <span className="text-gray-800">{formatPrice(orderInfo.previous_order_price)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Nuevo pedido:</span>
                <span className="text-gray-800">{formatPrice(orderInfo.final_price)}</span>
              </div>
              
              {orderInfo.total_discount && orderInfo.total_discount > 0 ? (
                <div className="flex justify-between text-green-600">
                  <span>Descuentos:</span>
                  <span>-{formatPrice(orderInfo.total_discount)}</span>
                </div>
              ) : null}
              
              {orderInfo.price_difference !== undefined && (
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="font-semibold text-gray-700">
                    {orderInfo.price_difference === 0 ? 'Sin cambio de precio' : orderInfo.price_difference < 0 ? 'Devolver:' : 'Diferencia:'}
                  </span>
                  <span className={`font-bold ${
                    orderInfo.price_difference === 0 ? 'text-gray-600' : 
                    orderInfo.price_difference < 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {orderInfo.price_difference === 0 ? '' : formatPrice(Math.abs(orderInfo.price_difference))}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mensaje informativo */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
          <div className="flex items-start gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-xs text-blue-800">
              Recibirás un correo electrónico en <span className="font-medium">{orderInfo.email}</span> con la confirmación de los cambios.
            </p>
          </div>
        </div>

        {/* Botón de cerrar */}
        <button
          onClick={onClose}
          className="w-full py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors shadow-sm"
        >
          Entendido
        </button>
      </div>
    </div>
  );
}